import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Trash2, Plus, Search, Ban } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState } from '@/components/ListStates';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CountryPhoneInput } from '@/components/CountryPhoneInput';
import { optOutsService, WhatsappOptOut } from '@/services/whatsapp-broadcasts.service';

const SOURCE_BADGE: Record<string, string> = {
  STOP_KEYWORD: 'bg-warning-soft text-warning',
  MANUAL: 'bg-info-soft text-info',
  COMPLAINT: 'bg-danger-soft text-danger',
};

type Props = {
  /** Rendered inside WhatsappToolsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function OptOutsRegistry({ embedded = false }: Props = {}) {
  const [data, setData] = useState<{ items: WhatsappOptOut[]; total: number; page: number; limit: number } | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [del, setDel] = useState<WhatsappOptOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const refresh = () => {
    setLoading(true);
    setLoadError(false);
    optOutsService
      .list(page, 25, q || undefined)
      .then(setData)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [page, q]);

  const handleDelete = async () => {
    if (!del) return;
    try {
      await optOutsService.remove(del.phoneE164);
      toast.success('Removed from opt-out list');
      setDel(null);
      refresh();
    } catch {
      // axios interceptor surfaces backend message
    }
  };

  return (
    <div>
      {embedded ? (
        <div className="flex justify-end px-7 pt-5">
          <Button iconLeft={<Plus size={14} />} onClick={() => setShowAdd(true)}>
            Add manually
          </Button>
        </div>
      ) : (
        <Topbar
          title="WhatsApp opt-outs"
          description="Numbers automatically excluded from every broadcast. STOP-keyword entries come from inbound webhooks; you can also add manually."
          right={
            <Button iconLeft={<Plus size={14} />} onClick={() => setShowAdd(true)}>
              Add manually
            </Button>
          }
        />
      )}

      <div className={clsx('px-7', embedded ? 'pb-24 pt-4' : 'py-5')}>
        <Card padded>
          {/* Filters */}
          <div className="mb-4 flex items-center gap-3">
            <div className="relative max-w-sm flex-1">
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search by phone number…"
                iconLeft={<Search size={14} />}
              />
            </div>
            {data && (
              <span className="text-2xs text-text-muted">
                {data.total.toLocaleString()} total
              </span>
            )}
          </div>

          {/* Table */}
          {loadError && !loading && (
            <LoadErrorState bare what="the opt-out list" onRetry={refresh} />
          )}

          {loading && !data && !loadError && (
            <div className="py-12 text-center text-sm text-text-muted">Loading…</div>
          )}

          {!loadError && data && data.items.length === 0 && !loading && (
            <EmptyState
              icon={<Ban size={28} />}
              title={q ? 'No matching opt-outs' : 'No opt-outs yet'}
              description={
                q
                  ? 'Try a different search term.'
                  : 'Recipients who reply STOP / UNSUBSCRIBE / REMOVE / CANCEL get added here automatically. You can also add numbers manually.'
              }
              action={
                q ? (
                  <Button variant="secondary" onClick={() => { setQ(''); setPage(1); }}>
                    Clear search
                  </Button>
                ) : (
                  <Button iconLeft={<Plus size={14} />} onClick={() => setShowAdd(true)}>
                    Add manually
                  </Button>
                )
              }
            />
          )}

          {!loadError && data && data.items.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-2xs font-semibold uppercase tracking-wider text-text-muted">
                      <th className="px-3 py-2.5">Phone</th>
                      <th className="px-3 py-2.5">Source</th>
                      <th className="px-3 py-2.5">Received</th>
                      <th className="px-3 py-2.5">Note</th>
                      <th className="px-3 py-2.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((row) => (
                      <tr key={row.optOutUid} className="border-b border-border/50 last:border-0 hover:bg-surface-alt/50">
                        <td className="px-3 py-2.5 font-mono tabular-nums text-text-primary">{row.phoneE164}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold ${SOURCE_BADGE[row.source] ?? 'bg-surface-alt text-text-muted'}`}>
                            {row.source.toLowerCase().replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-text-muted">
                          {new Date(row.receivedAt).toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-text-secondary">{row.note ?? '—'}</td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            onClick={() => setDel(row)}
                            className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                            aria-label="Remove"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
            </>
          )}
        </Card>
      </div>

      <AddOptOutModal open={showAdd} onClose={() => setShowAdd(false)} onAdded={refresh} />

      <ConfirmDialog
        open={!!del}
        title="Remove from opt-out list?"
        message={
          del
            ? `Removing ${del.phoneE164} means they'll be eligible for future broadcasts again. Only do this if they've explicitly asked to re-subscribe.`
            : ''
        }
        danger
        onConfirm={handleDelete}
        onCancel={() => setDel(null)}
      />
    </div>
  );
}

function AddOptOutModal({
  open, onClose, onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [country, setCountry] = useState('AE');
  const [number, setNumber] = useState('');
  const [e164, setE164] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setNumber(''); setE164(''); setNote(''); setCountry('AE'); };
  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!e164) { toast.error('Enter a valid phone number'); return; }
    setSubmitting(true);
    try {
      await optOutsService.add(e164, country, note || undefined);
      toast.success('Added to opt-out list');
      handleClose();
      onAdded();
    } catch {
      // interceptor surfaces
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} title="Add opt-out manually">
      <div className="space-y-4 px-6 py-5">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-text-secondary">Phone number</label>
          <CountryPhoneInput
            countryCode={country}
            number={number}
            onChange={(v) => { setCountry(v.countryCode); setNumber(v.number); setE164(v.e164); }}
            placeholder="50 123 4567"
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-text-secondary">Note (optional)</label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. requested removal via support"
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-border -mx-6 -mb-5 px-6 pt-4 pb-4 mt-2">
          <Button variant="ghost" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={!e164}>Add</Button>
        </div>
      </div>
    </Modal>
  );
}
